import React, { useCallback, useContext, useMemo, useState } from "react"
import { BSPatioForm } from "../../../../../../../../../core/dto/beem-shot/BSPatio/BSPatioForm"
import { BSPatio } from "../../../../../../../../../core/dto/beem-shot/BSPatio/BSPatio"
import { Children } from "../../../../../../../../../components/miscellianous/children"
import { BSPatioContext } from "./BSPatioContext"
import { BSLevelForm } from "../../../../../../../../../core/dto/beem-shot/BSBuildingLevel/BSLevelForm"
import { BSLevelContext } from "../../BSZoneAndLevelPage/context/BSLevelContext"

function dtoToForm(dto: BSPatio | undefined): BSPatioForm {
  return dto
    ? {
        surface: dto.surface,
        impactedFloor: dto.impactedFloor,
        startingLevelNumber: dto.startingLevelNumber,
      }
    : {
        surface: 0,
        impactedFloor: 0,
        startingLevelNumber: 0,
      }
}

export const BSPatioFormContext = React.createContext<BSPatioFormStore>({} as BSPatioFormStore)

export function BSPatioFormContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const { groupLevelSuperstructureList } = useContext(BSLevelContext)
  const { bsPatioList, setBSPatioList, submitBSPatioDtoList } = useContext(BSPatioContext)

  const [errors, setErrors] = useState<Record<string, string | undefined>>({})
  const [isDisplayNewBSPatioForm, setIsDisplayNewBSPatioForm] = useState<boolean>(false)
  const [bsPatioForm, setBSPatioForm] = useState<BSPatioForm>(dtoToForm(undefined))
  const [currentEditedIndex, setCurrentEditedIndex] = useState<number | undefined>()

  const superstructureLevels: BSLevelForm[] = useMemo(
    () => groupLevelSuperstructureList.flatMap((group) => group.levelList),
    [groupLevelSuperstructureList]
  )

  const clearError: (fieldName: string) => void = useCallback((fieldName) => {
    setErrors((prev) => {
      const { [fieldName]: _, ...rest } = prev
      return rest
    })
  }, [])

  const getRemainingLevels: (startingLevelNumber: number) => number = useCallback(
    (startingLevelNumber) => superstructureLevels.filter((level) => level.levelNumber > startingLevelNumber).length,
    [superstructureLevels]
  )

  const bsPatioFormToDto: (form: BSPatioForm) => BSPatio = useCallback(
    (form) => ({
      surface: form.surface,
      impactedFloor: form.impactedFloor,
      startingLevelNumber: form.startingLevelNumber,
    }),
    []
  )

  const addNewBSPatioToListState: (newBSPatioForm: BSPatioForm) => void = useCallback(
    (newBSPatioForm) => {
      const newBSPatio = bsPatioFormToDto(newBSPatioForm)
      setBSPatioList((prev) => [...prev, newBSPatio])
    },
    [bsPatioFormToDto, setBSPatioList]
  )

  const updateBSPatioFormList: (updatedBSPatioForm: BSPatioForm, updatedFormIndex: number) => Promise<any> = useCallback(
    (updatedBSPatioForm, updatedFormIndex) => {
      const newList = bsPatioList.map((patio, index) =>
        index === updatedFormIndex ? bsPatioFormToDto(updatedBSPatioForm) : patio
      )
      return submitBSPatioDtoList(newList)
    },
    [bsPatioFormToDto, bsPatioList, submitBSPatioDtoList]
  )

  const fieldsToValidate: Array<{
    fieldName: string
    getValue: (form: BSPatioForm) => number
    isValueValid: (value: number, form: BSPatioForm) => boolean
    errorMessage: string
  }> = useMemo(
    () => [
      {
        fieldName: "surface",
        getValue: (form) => form.surface,
        isValueValid: (value, _form) => value !== 0,
        errorMessage: "La surface ne doit pas être égale à zéro",
      },
      {
        fieldName: "startingLevelNumber",
        getValue: (form) => form.startingLevelNumber,
        isValueValid: (value, _form) => value <= superstructureLevels.length - 1,
        errorMessage: "Le niveau de départ ne peut dépasser le nombre de niveaux en superstructure.",
      },
      {
        fieldName: "impactedFloor",
        getValue: (form) => form.impactedFloor,
        isValueValid: (value: number, form: BSPatioForm) =>
          value > 0 && value <= getRemainingLevels(form.startingLevelNumber) + 1,
        errorMessage:
          "Le nombre d'étages impactés doit être compris entre 1 et le nombre de niveaux restants par rapport au niveau de départ.",
      },
    ],
    [superstructureLevels, getRemainingLevels]
  )

  const validateNonZero: (fieldName: string, fieldValue: number) => void = useCallback(
    (fieldName, fieldValue) => {
      if (fieldValue !== 0) {
        clearError(fieldName)
      }
    },
    [clearError]
  )

  const validateImpactedLevelNumber: (fieldName: string, fieldValue: number) => void = useCallback(
    (fieldName, fieldValue) => {
      if (fieldValue > 0 && fieldValue <= getRemainingLevels(fieldValue) + 1) {
        clearError(fieldName)
      }
    },
    [getRemainingLevels, clearError]
  )

  const validateStartingLevelNumber: (fieldName: string, fieldValue: number) => void = useCallback(
    (fieldName, fieldValue) => {
      if (fieldValue <= superstructureLevels.length) {
        clearError(fieldName)
      }
    },
    [superstructureLevels, clearError]
  )

  const validateBSPatioForm: (form: BSPatioForm) => boolean = useCallback(
    (form) => {
      let isValid = true

      fieldsToValidate.forEach(({ fieldName, getValue, isValueValid, errorMessage }) => {
        const value = getValue(form)

        if (!isValueValid(value, form)) {
          isValid = false
          setErrors((prev) => ({ ...prev, [fieldName]: errorMessage }))
        }
      })

      return isValid
    },
    [fieldsToValidate]
  )

  const fieldValidators: Partial<Record<keyof BSPatioForm, (field: keyof BSPatioForm, value: number) => void>> = useMemo(
    () => ({
      surface: validateNonZero,
      startingLevelNumber: validateStartingLevelNumber,
      impactedFloor: validateImpactedLevelNumber,
    }),
    [validateNonZero, validateStartingLevelNumber, validateImpactedLevelNumber]
  )

  const setFieldValue: (fieldName: keyof BSPatioForm, value: number) => void = useCallback((fieldName, value) => {
    setBSPatioForm((prev) => (prev ? { ...prev, [fieldName]: value } : prev))
  }, [])

  const handleFieldChange: (field: keyof BSPatioForm, value: number) => void = useCallback(
    (field, value) => {
      setBSPatioForm((prev) => ({ ...prev, [field]: value }))

      fieldValidators[field]?.(field, value)
    },
    [fieldValidators]
  )

  const selectPatio: (bsPatio: BSPatio | undefined) => void = useCallback((bsPatio) => {
    setBSPatioForm(dtoToForm(bsPatio))
  }, [])

  const resetForm: () => void = useCallback(() => {
    setIsDisplayNewBSPatioForm(false)
    setErrors({})
    setBSPatioForm(dtoToForm(undefined))
  }, [])

  const isCurrentlyEditedIndex: (index: number) => boolean = useCallback(
    (index) => index === currentEditedIndex,
    [currentEditedIndex]
  )

  const bsPatioFormStore: BSPatioFormStore = useMemo(
    () => ({
      selectedBSPatioForm: bsPatioForm,
      errors,
      setErrors,
      isCurrentlyEditedIndex,
      setCurrentEditedIndex,
      isDisplayNewBSPatioForm,
      setIsDisplayNewBSPatioForm,
      fieldValidators,
      selectPatio,
      validateBSPatioForm,
      setSelectedBSPatioForm: setBSPatioForm,
      setFieldValue,
      bsPatioDtoToForm: dtoToForm,
      addNewBSPatioToList: addNewBSPatioToListState,
      updateBSPatioFormList,
      resetForm,
      handleFieldChange,
      bsPatioFormToDto,
    }),
    [
      bsPatioForm,
      errors,
      setErrors,
      isCurrentlyEditedIndex,
      isDisplayNewBSPatioForm,
      setIsDisplayNewBSPatioForm,
      fieldValidators,
      selectPatio,
      validateBSPatioForm,
      setBSPatioForm,
      setFieldValue,
      addNewBSPatioToListState,
      updateBSPatioFormList,
      resetForm,
      handleFieldChange,
      bsPatioFormToDto,
    ]
  )

  return <BSPatioFormContext.Provider value={bsPatioFormStore}>{children}</BSPatioFormContext.Provider>
}

export interface BSPatioFormStore {
  selectedBSPatioForm: BSPatio
  setSelectedBSPatioForm: React.Dispatch<React.SetStateAction<BSPatio>>
  errors: Record<string, string | undefined>
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string | undefined>>>
  fieldValidators: Partial<Record<keyof BSPatioForm, (field: keyof BSPatioForm, value: number) => void>>
  isDisplayNewBSPatioForm: boolean
  setIsDisplayNewBSPatioForm: React.Dispatch<React.SetStateAction<boolean>>
  setCurrentEditedIndex: React.Dispatch<React.SetStateAction<number | undefined>>

  isCurrentlyEditedIndex(index: number): boolean

  selectPatio(bsPatio: BSPatio | undefined): void

  setFieldValue(fieldName: keyof BSPatioForm, value: number): void

  validateBSPatioForm(bsPatioForm: BSPatioForm): boolean

  bsPatioDtoToForm(dto: BSPatio): BSPatioForm

  addNewBSPatioToList(newBSPatioForm: BSPatioForm): void

  updateBSPatioFormList(updatedForm: BSPatioForm, index: number): Promise<any>

  resetForm(): void

  handleFieldChange(field: keyof BSPatioForm, value: number): void

  bsPatioFormToDto(form: BSPatioForm): BSPatio
}
